import type { ElementDescriptor } from '@/events/shared/ElementDescriptor';

const MAX_TEXT_LENGTH = 80;

export function describeElement(
  element: Element | null
): ElementDescriptor | undefined {
  if (!element) return undefined;

  const htmlElement = element as HTMLElement;
  const classes = element.classList?.length
    ? Array.from(element.classList)
    : undefined;

  let text: string | undefined;
  const rawText =
    htmlElement.innerText?.trim() ?? htmlElement.textContent?.trim();
  if (rawText) {
    text =
      rawText.length > MAX_TEXT_LENGTH
        ? `${rawText.slice(0, MAX_TEXT_LENGTH)}…`
        : rawText;
  }

  const dataset: Record<string, string> = {};
  if (htmlElement.dataset) {
    for (const [key, value] of Object.entries(htmlElement.dataset)) {
      if (value !== undefined) {
        dataset[key] = value;
      }
    }
  }

  return {
    tagName: element.tagName?.toLowerCase(),
    id: element.id || undefined,
    classes,
    text,
    role: htmlElement.getAttribute('role') ?? undefined,
    name: htmlElement.getAttribute('name') ?? undefined,
    dataset: Object.keys(dataset).length > 0 ? dataset : undefined,
  };
}

/** Fingerprint estável alinhado ao algoritmo legado (tag + id + classes + data-*). */
export function getElementFingerprint(el: EventTarget | null): string {
  if (!el || !(el instanceof Element)) return 'unknown';

  const tag = el.tagName.toLowerCase();
  const id = el.id ? `#${el.id}` : '';
  const classes =
    el.className && typeof el.className === 'string'
      ? `.${el.className.trim().split(/\s+/).slice(0, 3).join('.')}`
      : '';

  const dataAttrs: string[] = [];
  if (typeof el.getAttributeNames === 'function') {
    el.getAttributeNames()
      .filter(name => name.startsWith('data-'))
      .slice(0, 5)
      .sort()
      .forEach(name => {
        const value = el.getAttribute(name);
        if (value != null) {
          dataAttrs.push(`${name}=${value.slice(0, 50)}`);
        }
      });
  }

  return [tag, id, classes, dataAttrs.join('|')].filter(Boolean).join('');
}
